import { FC } from 'react';
import { Nothing } from 'components/Nothing';
import styles from './OrdersListTablet.module.scss';
import { Checkbox, Paper, Stack } from '@mantine/core';
import { AvatarMobile } from 'components/AvatarMobile';
import { objTextColor, Status } from 'ui/CustomerOrderItem';
import { useNavigate } from 'react-router';
import { formatCustomDate, getDateAndMonth } from 'utils/date';
import { ReactComponent as IconAlert } from './img/iconAlertOutline.svg';
import { ReactComponent as IconClose } from './img/iconClose.svg';
import { ReactComponent as IconDone } from './img/iconDone.svg';
import { ReactComponent as IconDraft } from './img/iconDraft.svg';
import { ReactComponent as IconTime } from './img/iconTime.svg';
import classNames from 'classnames';
import { FormatAmount } from 'ui/FormatAmount';
import { TextWithTooltip } from 'hooks/TextWithTooltip';

interface OrdersListTablet {
  data: any;
  withCheckbox?: boolean;
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  intersectionComponent: JSX.Element | null;
}

export const objIcons: any = {
  moderation_in_progress: IconTime,
  moderation_failed: IconClose,
  worker_invited_by_customer: IconAlert,
  worker_approved_by_customer: IconAlert,
  worker_approved_order: IconTime,
  worker_set_volume: IconTime,
  customer_set_volume: IconTime,
  worker_selected_by_platform: IconTime,
  customer_started_order: IconTime,
  customer_finished_order: IconTime,
  worker_finished_order: IconAlert,
  work_success: IconAlert,
  act_signed_by_worker: IconAlert,
  payment_in_progress: IconTime,
  act_signed_by_customer: IconTime,
  payment_failed: IconClose,
  payment_returned: IconClose,
  payment_success: IconDone,
  order_canceled: IconClose,
  order_draft: IconDraft,
  order_published: IconTime,
  worker_rejected_by_customer: IconTime,
  order_canceled_by_platform: IconClose,
};

export const OrdersListTablet: FC<OrdersListTablet> = ({
  data,
  withCheckbox = false,
  selected,
  setSelected,
  intersectionComponent,
}) => {
  const navigate = useNavigate();
  const handleNaviage = (id: string) => {
    navigate(`/orders/${id}`);
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  return (
    <Stack align='stretch' justify='center' gap='sm'>
      {data?.length ? (
        data.map((item: any) => (
          <Paper
            className={styles.root}
            shadow='0'
            p='12px'
            radius='md'
            key={item.id}
            onClick={() => handleNaviage(item?.id)}>
            <div className={styles.items}>
              <div className={styles.withCheckbox}>
                {withCheckbox && (
                  <div
                    className={classNames(
                      styles.root_checkbox,
                      selected.includes(item.id) && styles.with_checkbox,
                    )}>
                    <Checkbox
                      color='#002CFB'
                      checked={selected.includes(item.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleSelect(item.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      styles={{
                        input: {
                          border: selected.includes(item.id)
                            ? '2px solid #002CFB'
                            : '2px solid rgba(173, 191, 223, 0.4)',
                          cursor: 'pointer',
                        },
                      }}
                    />
                  </div>
                )}
              </div>
              <div className={styles.containerTitle}>
                <span className={styles.first}>№{item?.number}</span>
                <p className={styles.first}>Услуга</p>{' '}
                <p className={styles.first}>Сумма, ₽</p>
                <p className={styles.first}>Дата оплаты</p>
              </div>
              <div className={styles.containerText}>
                <div className={styles.second}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      padding: '0px 12px 0px 0px',
                    }}>
                    <div className={styles.name}>
                      <AvatarMobile
                        size={24}
                        name={item?.worker?.name}
                        surname={item?.worker?.surname}
                        avatar={item?.worker?.avatar}
                        isDeleted={item?.worker?.is_deleted}
                      />

                      <TextWithTooltip
                        content={
                          !item?.worker?.surname
                            ? item?.worker?.name
                            : item?.worker?.patronymic
                            ? `${item?.worker?.surname} ${item?.worker?.name} ${item?.worker?.patronymic}`
                            : `${item?.worker?.surname} ${item?.worker?.name}`
                        }
                        className={styles.truncate}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.second}>
                  <TextWithTooltip
                    content={item?.order_type}
                    className={styles.truncate}
                  />
                </div>
                <div className={styles.second}>
                  <div className={styles.right}>
                    <span className={styles.amount}>
                      <FormatAmount amount={item?.amount} />
                    </span>
                  </div>
                </div>
                <div className={styles.second}>
                  <div className={styles.right}>
                    <span>
                      {item?.worker_payment_date
                        ? formatCustomDate(item?.worker_payment_date)
                        : ''}
                    </span>
                  </div>
                </div>
              </div>
              <div className={styles.containerStatus}>
                <p
                  className={styles.first}
                  style={{ justifyContent: 'flex-end', width: '100%' }}>
                  {getDateAndMonth(item?.created_at)}
                </p>
                <div className={styles.first}>{''}</div>
                <div className={styles.first}>{''}</div>
                <div className={styles.second}>
                  <div className={styles.truncate}>
                    <Status
                      title={item.status?.text}
                      color={
                        item.status.color_bg || objTextColor[item.status?.value] || '#000'
                      }
                      Icon={
                        item.status?.text === 'Требуется выбрать исполнителя'
                          ? IconAlert
                          : objIcons[item.status?.value] || null
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </Paper>
        ))
      ) : (
        <Nothing style={{ height: '320px' }} title='Ничего не найдено' />
      )}
      {intersectionComponent}
    </Stack>
  );
};
